import type { JobApplication, Job } from '@cibli/shared';

import { isAbortError } from '@cibli/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { onDataChanged } from '@/lib/dataEvents';

import { getUserJobApplications } from '../services/job';

const APPLICATIONS_REFETCH_DEBOUNCE_MS = 300;

export function useUserJobApplications() {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchApplications = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setIsLoading(true);
      setError(null);
      const data = await getUserJobApplications(controller.signal);
      if (controller.signal.aborted) return;
      setApplications(data);
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err instanceof Error ? err : new Error('Failed to fetch applications'));
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    return onDataChanged('applications-changed', () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        void fetchApplications();
      }, APPLICATIONS_REFETCH_DEBOUNCE_MS);
    });
  }, [fetchApplications]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      abortRef.current?.abort();
    };
  }, []);

  const hasAppliedToJob = (job: Job | { id: string }): boolean => {
    return applications.some((app: JobApplication) => app.jobId === job.id);
  };

  const refetch = () => {
    void fetchApplications();
  };

  return {
    applications,
    isLoading,
    error,
    hasAppliedToJob,
    refetch,
  };
}
